import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Accessibility, Banknote, Pill, ScrollText, Stethoscope, Users } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTable } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import type { EntityLookupValue } from "@/components/shared/EntityLookup";
import { formatDateBr } from "@/lib/format";
import { getInstituicaoRegistro } from "./formApi";
import { getInteracoesInstituicao, getOnboardingCards } from "./fichaApi";
import { COBRANCA_PATH, getInstituicaoLookup, type Cobranca, type CobrancaFiltro } from "@/features/cobranca/api";
import { SITUACAO_COBRANCA_BADGE, formatValorCobranca } from "@/features/cobranca/format";
import { TIPO_INTERACAO_LABEL } from "@/features/instituicao-interacao/labels";
import { Badge } from "@/components/ui/badge";
import { AcessosFicha } from "@/features/acessos/AcessosFicha";

// Porte de app/controllers/ficha-instituicao.controller.js +
// app/templates/instituicao/ficha-instituicao.html. Dashboard só-leitura —
// sem ações, sem formulário. A tabela de cobranças usa usePagedQuery +
// DataTable (é paginada de verdade no legado, via PaginationService). O
// legado filtra mandando o objeto `instituicao` inteiro no corpo; só
// `instituicaoId` o backend ignora — por isso o filtro leva os dois, como
// ConsultaCobranca. A lista de interações não é paginada — o legado busca o
// array inteiro e ordena client-side por dataInteracao desc.
const CARDS = [
  { key: "quantidadeUsuarios" as const, icon: Users, label: "Usuários" },
  { key: "quantidadeProfissionais" as const, icon: Stethoscope, label: "Profissionais" },
  { key: "quantidadeMedicamentos" as const, icon: Pill, label: "Medicamentos" },
  { key: "quantidadeIdosos" as const, icon: Accessibility, label: "Idosos" },
  { key: "quantidadeEvolucoes" as const, icon: ScrollText, label: "Evoluções" },
  { key: "quantidadeAtrasadas" as const, icon: Banknote, label: "Parcelas atrasadas" },
];

const COBRANCA_COLUMNS: ColumnDef<Cobranca, unknown>[] = [
  { id: "instituicao", header: "Instituição", enableSorting: false, cell: ({ row }) => row.original.instituicao?.nomeFantasia },
  { accessorKey: "dataEmissao", header: "Data Emissão", enableSorting: false, cell: ({ row }) => formatDateBr(row.original.dataEmissao) },
  { accessorKey: "dataVencimento", header: "Data Vencimento", enableSorting: true, cell: ({ row }) => formatDateBr(row.original.dataVencimento) },
  { accessorKey: "valorCobranca", header: "Valor", enableSorting: false, cell: ({ row }) => formatValorCobranca(row.original.valorCobranca) },
  {
    accessorKey: "situacaoCobranca",
    header: "Situação",
    enableSorting: false,
    cell: ({ row }) => {
      const badge = SITUACAO_COBRANCA_BADGE[row.original.situacaoCobranca];
      return badge ? <Badge className={badge.className}>{badge.label}</Badge> : "N/D";
    },
  },
];

// Só monta com a instituição já carregada: o usePagedQuery guarda o filtro
// inicial em useState, então ele precisa nascer completo.
function CobrancasDaInstituicao({ id, instituicao }: { id: number; instituicao: EntityLookupValue }) {
  const query = usePagedQuery<Cobranca, CobrancaFiltro>({
    key: ["cobranca", "ficha", id],
    path: COBRANCA_PATH,
    initialFiltro: { seIgnoradas: "N", instituicao, instituicaoId: id },
    initialSort: { field: "dataVencimento", direction: "DESC" },
  });
  return <DataTable query={query} columns={COBRANCA_COLUMNS} getRowId={(c) => c.id} emptyMessage="Nenhuma cobrança encontrada." />;
}

export default function FichaInstituicao() {
  const { idInstituicao } = useParams<{ idInstituicao: string }>();
  const id = Number(idInstituicao);

  const { data: instituicao } = useQuery({
    queryKey: ["instituicao", "registro", idInstituicao],
    queryFn: () => getInstituicaoRegistro(id),
  });

  const { data: instituicaoLookup } = useQuery({
    queryKey: ["cobranca", "instituicao-lookup", id],
    queryFn: () => getInstituicaoLookup(id),
  });

  const { data: cards } = useQuery({
    queryKey: ["instituicao", "onboarding-cards", idInstituicao],
    queryFn: () => getOnboardingCards(id),
  });

  const { data: interacoes } = useQuery({
    queryKey: ["instituicao", "interacoes", idInstituicao],
    queryFn: () => getInteracoesInstituicao(id),
  });

  const interacoesOrdenadas = [...(interacoes ?? [])].sort((a, b) => (a.dataInteracao < b.dataInteracao ? 1 : -1));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4 shadow-card">
        <img
          src="https://mlx2yoslrjes.i.optimole.com/LiDCupE-CsvKlRVk/w:auto/h:auto/q:mauto/https://silpi.com.br/wp-content/uploads/2021/03/favicon.png"
          alt="Silpi"
          className="h-16 w-16"
        />
        <div>
          <h1 className="text-lg font-semibold text-foreground">{instituicao?.nomeFantasia ?? "…"}</h1>
          <p className="text-sm text-muted-foreground">
            {instituicao?.municipio}/{instituicao?.estado} · Cliente desde{" "}
            {instituicao?.dataInicio ? formatDateBr(instituicao.dataInicio) : "—"}
          </p>
          <p className="text-sm font-medium text-foreground">Health score {cards?.healthScore ?? "—"}</p>
        </div>
      </div>

      {Number.isFinite(id) && <AcessosFicha idInstituicao={id} />}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {CARDS.map(({ key, icon: Icon, label }) => (
          <div key={key} className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card p-4 text-center shadow-card">
            <Icon className="h-8 w-8 text-primary" />
            <span className="text-2xl font-semibold text-foreground">{cards?.[key] ?? 0}</span>
            <span className="text-sm text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <h2 className="mb-2 text-base font-semibold text-foreground">Cobranças</h2>
          {instituicaoLookup ? (
            <CobrancasDaInstituicao id={id} instituicao={instituicaoLookup} />
          ) : (
            <Skeleton className="h-48 w-full" />
          )}
        </div>

        <div>
          <h2 className="mb-2 text-base font-semibold text-foreground">Interações</h2>
          {interacoesOrdenadas.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground shadow-card">
              Nenhuma interação recente
            </p>
          ) : (
            <div className="space-y-3">
              {interacoesOrdenadas.map((item) => (
                <div key={item.id} className="rounded-xl border border-border bg-card p-3 shadow-card">
                  <p className="font-medium text-foreground">
                    {item.usuarioResponsavel?.nome} <span className="text-muted-foreground">{TIPO_INTERACAO_LABEL[item.tipoInteracao]}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">{formatDateBr(item.dataInteracao)}</p>
                  <p className="text-sm text-muted-foreground">{item.instituicao?.nomeFantasia}</p>
                  <p className="text-sm text-foreground">{item.descricao}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-base font-semibold text-foreground">Detalhamento health score</h2>
        <ul className="list-disc space-y-1 rounded-xl border border-border bg-card p-4 pl-9 text-sm text-foreground shadow-card">
          <li>Login últimos 30 dias: {cards?.hsDto?.login ?? "—"}</li>
          <li>Idosos totais: {cards?.hsDto?.idosos ?? "—"}</li>
          <li>Evoluções na última semana: {cards?.hsDto?.evolucoes ?? "—"}</li>
          <li>Cadastra medicamentos: {cards?.hsDto?.medicamentos ?? "—"}</li>
          <li>Usa estoque produtos: {cards?.hsDto?.estoque ?? "—"}</li>
          <li>Usa financeiro: {cards?.hsDto?.financeiro ?? "—"}</li>
          <li>Inadimplência: {cards?.hsDto?.inadimplencia ?? "—"}</li>
        </ul>
      </div>
    </div>
  );
}
